import { useCallback, useMemo, useState } from "react";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  useEntityList,
  type DataTableColumn,
} from "@pericles/framework-frontend";

import { NAV_LABELS, PROJECT_LABEL } from "../config/brand";

import { EntityDeleteButton } from "./EntityDeleteButton";
import { PageShell } from "./ui";

interface ProjectListItem {
  id: string | number;
  name?: string;
  code?: string;
  customer?: unknown;
  customerId?: string | number;
  status?: string;
  startDate?: string;
  endDate?: string;
}

interface ProjectsListScreenProps<TFilter = Record<string, unknown>> {
  entityName: string;
  activeCustomerId: string;
  onActiveCustomerChange: (customerId: string) => void;
  filters?: TFilter;
  pageSize?: number;
  onRowClick?: (row: ProjectListItem) => void;
  onEditClick?: (row: ProjectListItem) => void;
  title?: string;
}

function getCustomerId(project: ProjectListItem): string | null {
  if (project.customerId != null) {
    return String(project.customerId);
  }

  const customer = project.customer;
  if (customer && typeof customer === "object" && "id" in customer) {
    const id = (customer as { id?: string | number }).id;
    if (id != null) {
      return String(id);
    }
  }

  return null;
}

function getCustomerName(customer: ProjectListItem["customer"]): string {
  if (customer == null) return "";
  if (typeof customer === "string") return customer;

  if (typeof customer === "object") {
    if ("name" in customer && typeof (customer as any).name === "string") {
      return (customer as any).name as string;
    }
    if ("title" in customer && typeof (customer as any).title === "string") {
      return (customer as any).title as string;
    }
  }

  return String(customer);
}

export function ProjectsListScreen<TFilter = Record<string, unknown>>({
  entityName,
  activeCustomerId,
  onActiveCustomerChange,
  filters,
  pageSize = 20,
  onRowClick,
  onEditClick,
  title = NAV_LABELS.projects,
}: ProjectsListScreenProps<TFilter>): JSX.Element {
  const [page, setPage] = useState(0);

  const pluralLabel = NAV_LABELS.projects;
  const lowerPluralLabel = pluralLabel.toLowerCase();
  const lowerSingularLabel = PROJECT_LABEL.toLowerCase();

  const mergedFilters = useMemo(() => {
    if (activeCustomerId === "all") return filters;
    const baseFilters = (filters ?? {}) as Record<string, unknown>;
    return { ...baseFilters, customerId: activeCustomerId } as TFilter;
  }, [activeCustomerId, filters]);

  const { data, total, isLoading, isError, error, refetch } = useEntityList<ProjectListItem, TFilter>({
    entity: entityName,
    filters: mergedFilters,
    page,
    pageSize,
  });

  const filteredRows = useMemo(() => {
    const items = data ?? [];
    if (activeCustomerId === "all") return items;
    return items.filter((project) => getCustomerId(project) === activeCustomerId);
  }, [activeCustomerId, data]);

  const displayTotal = typeof total === "number" ? total : filteredRows.length;

  const columns = useMemo<DataTableColumn<ProjectListItem>[]>(
    () => [
      { id: "name", header: "Name", accessor: (row) => row.name ?? "" },
      { id: "code", header: "Code", accessor: (row) => row.code ?? "" },
      { id: "customer", header: "Customer", accessor: (row) => getCustomerName(row.customer) },
      { id: "status", header: "Status", accessor: (row) => row.status ?? "" },
      { id: "startDate", header: "Start Date", accessor: (row) => row.startDate ?? "" },
      { id: "endDate", header: "End Date", accessor: (row) => row.endDate ?? "" },
      {
        id: "actions",
        header: "Actions",
        accessor: (row) => (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="button button-primary button-sm"
              onClick={(event) => {
                event.stopPropagation();
                onEditClick?.(row);
              }}
            >
              Edit
            </button>
            <EntityDeleteButton
              entityName={entityName}
              entityId={row.id}
              confirmMessage={`Delete this ${lowerSingularLabel}? This action cannot be undone.`}
              onDeleted={() => refetch()}
            />
          </div>
        ),
        width: 200,
      },
    ],
    [entityName, lowerSingularLabel, onEditClick, refetch],
  );

  const handleRowClick = useCallback(
    (row: ProjectListItem) => {
      const customerId = getCustomerId(row);
      if (customerId) {
        onActiveCustomerChange(customerId);
      }
      onRowClick?.(row);
    },
    [onActiveCustomerChange, onRowClick],
  );

  const errorNode = isError ? (
    <span className="text-sm text-red-600">
      {error instanceof Error ? error.message : `Failed to load ${lowerPluralLabel}`}
    </span>
  ) : undefined;

  return (
    <PageShell>
      <PageHeader title={title} />

      <Card>
        <DataTable<ProjectListItem>
          columns={columns}
          data={filteredRows}
          isLoading={isLoading}
          error={errorNode}
          page={page}
          pageSize={pageSize}
          totalCount={displayTotal}
          onPageChange={setPage}
          onRowClick={handleRowClick}
          rowClassName={(row) => (getCustomerId(row) === activeCustomerId ? "data-table__row--active" : undefined)}
          emptyState={
            <EmptyState
              title={`No ${lowerPluralLabel}`}
              description={`Create a ${lowerSingularLabel} to get started.`}
            />
          }
        />
      </Card>
    </PageShell>
  );
}
